import React, { useContext } from 'react';
import '../../GridEditor/ItemBuildProgression.css';
import GameAssetsContext from '../../../../XXX/context/GameAssetContext';

// Types for item build data
export interface StarterItem {
  item_id: number;
  name: string;
  pick_count: number;
}

export interface CoreItems {
  item1_id: number;
  item1_name: string;
  item2_id: number;
  item2_name: string;
  item3_id: number;
  item3_name: string;
  times_seen: number;
  pct_of_players: number;
}

export interface SupplementaryItem {
  item_id: number;
  item_name: string;
  most_common_slot: number;
  slot_buys: number;
  pct_in_that_slot: number;
}

export interface ItemBuildData {
  starter: StarterItem | null;
  coreItems: CoreItems;
  supplementaryItems: SupplementaryItem[];
  boots?: {
    item_id: number;
    name: string;
    pick_count: number;
  } | null;
  finalSupportItem?: {
    item_id: number;
    name: string;
    pick_count: number;
  } | null;
}

// Role type for display
type Role = 'top' | 'jgl' | 'mid' | 'bot' | 'sup';

const ROLE_TO_ICON_KEY: Record<Role, string> = {
  top: 'TOP',
  jgl: 'JUNGLE',
  mid: 'MIDDLE',
  bot: 'BOTTOM',
  sup: 'UTILITY'
};

interface Props {
  championName: string;
  itemBuild: ItemBuildData;
  teamColor: 'blue' | 'red';
  apiVersion: string;
  onItemClick?: (itemId: number, itemName: string) => void;
  onItemMouseEnter?: (itemId: number, itemName: string, event: React.MouseEvent) => void;
  onItemMouseMove?: (event: React.MouseEvent) => void;
  onItemMouseLeave?: () => void;
  onChangeItemBuild?: () => void;
  showChangeButton?: boolean;
  role?: Role; // Current role for display only
  // For custom builds: pass mainItems directly (array of item IDs)
  customMainItems?: string[];
  className?: string;
}

const ItemBuildDisplay: React.FC<Props> = ({
  championName,
  itemBuild,
  teamColor,
  apiVersion,
  onItemClick,
  onItemMouseEnter,
  onItemMouseMove,
  onItemMouseLeave,
  onChangeItemBuild,
  showChangeButton = false,
  role,
  customMainItems,
  className = ''
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  // Boot item IDs - used to filter duplicates
  const BOOTS_IDS = new Set([
    1001, // Basic Boots
    3006, 3009, 3020, 3047, 3111, 3117, 3158, // T1 boots
    223009, 223020, 223047, 223111, 223158, 223006 // T2 boots
  ]);
  
  const isBootsItem = (itemId: number): boolean => BOOTS_IDS.has(itemId);
  
  // Helper function to get item image URL
  const getItemImageUrl = (itemId: number) => {
    return `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/${itemId}.png`;
  };

  // Filter items to ensure only 1 pair of boots
  const filterToOneBoots = (items: { id: number; name: string }[]): { id: number; name: string }[] => {
    let hasBoots = false;
    return items.filter(item => {
      if (isBootsItem(item.id)) {
        if (hasBoots) {
          return false; // Skip duplicate boots
        }
        hasBoots = true;
      }
      return true;
    });
  };

  // Get all main items (either from customMainItems or from itemBuild)
  const getAllMainItems = (): { id: number; name: string }[] => {
    let items: { id: number; name: string }[] = [];
    
    // If custom main items are provided, use them
    if (customMainItems && customMainItems.length > 0) {
      items = customMainItems.map(id => ({
        id: parseInt(id, 10),
        name: `Item ${id}` // Name will be shown via tooltip
      }));
    } else {
      // Fallback: build from itemBuild data (core + supplementary only, no separate boots)
      // Add core items
      items.push({ id: itemBuild.coreItems.item1_id, name: itemBuild.coreItems.item1_name });
      items.push({ id: itemBuild.coreItems.item2_id, name: itemBuild.coreItems.item2_name });
      items.push({ id: itemBuild.coreItems.item3_id, name: itemBuild.coreItems.item3_name });
      
      // Add supplementary items (boots may be included here already)
      const addedIds = new Set(items.map(i => i.id));
      if (itemBuild.supplementaryItems) {
        for (const item of itemBuild.supplementaryItems) {
          if (!addedIds.has(item.item_id) && items.length < 6) {
            items.push({ id: item.item_id, name: item.item_name });
            addedIds.add(item.item_id);
          }
        }
      }
    }
    
    // HARD FILTER: Ensure only 1 pair of boots in the final list
    return filterToOneBoots(items).slice(0, 7);
  };

  const mainItems = getAllMainItems();
  
  // Split items: first 3 items, then remaining items (up to 4 for bot quest)
  const firstRowItems = mainItems.slice(0, 3);
  const secondRowItems = mainItems.slice(3);

  return (
    <div className={`champion-build ${teamColor}-champion ${className}`}>
      <div className="champion-header">
        <div className="champion-name">
          {championName}
          {role && gameWideAssets.roleIcons[ROLE_TO_ICON_KEY[role]] && (
            <img
              src={gameWideAssets.roleIcons[ROLE_TO_ICON_KEY[role]]}
              alt={role}
              className="role-indicator-icon"
              style={{ width: 16, height: 16, marginLeft: 6, opacity: 0.4, filter: 'grayscale(1)', verticalAlign: 'middle' }}
            />
          )}
        </div>
        {showChangeButton && onChangeItemBuild && (
          <button 
            className="change-build-button"
            onClick={onChangeItemBuild}
            title={`Change item build for ${championName}`}
          >
            <img src="/icons/gear-icon.svg" alt="Settings" className="gear-icon" />
          </button>
        )}
      </div>

      <div className="build-progression">
        
        {/* Starter Item */}
        <div className="build-phase starter-phase">
          <div className="items-row">
            {itemBuild.starter ? (
              <div 
                className="item-slot clickable-item"
                onClick={() => onItemClick?.(itemBuild.starter!.item_id, itemBuild.starter!.name)}
                onMouseEnter={(e) => onItemMouseEnter?.(itemBuild.starter!.item_id, itemBuild.starter!.name, e)}
                onMouseMove={onItemMouseMove}
                onMouseLeave={onItemMouseLeave}
                title={`${itemBuild.starter.name} (${itemBuild.starter.pick_count} picks)`}
              >
                <img 
                  src={getItemImageUrl(itemBuild.starter.item_id)} 
                  alt={itemBuild.starter.name}
                />
              </div>
            ) : (
              <div className="item-slot empty">
                <span>?</span>
              </div>
            )}
          </div>
        </div>

        {/* Arrow */}
        <div className="progression-arrow">
          <span>→</span>
        </div>

        {/* First 3 Items */}
        <div className="build-phase core-phase">
          <div className="items-row">
            {firstRowItems.map((item, index) => (
              <div 
                key={`item-${index}-${item.id}`}
                className="item-slot clickable-item"
                onClick={() => onItemClick?.(item.id, item.name)}
                onMouseEnter={(e) => onItemMouseEnter?.(item.id, item.name, e)}
                onMouseMove={onItemMouseMove}
                onMouseLeave={onItemMouseLeave}
                title={item.name}
              >
                <img 
                  src={getItemImageUrl(item.id)} 
                  alt={item.name}
                />
              </div>
            ))}
            {/* Fill empty slots if less than 3 items */}
            {Array.from({ length: Math.max(0, 3 - firstRowItems.length) }).map((_, idx) => (
              <div key={`empty-first-${idx}`} className="item-slot empty">
                <span>?</span>
              </div>
            ))}
          </div>
        </div>

        {/* Arrow (only if there are more items) */}
        {secondRowItems.length > 0 && (
          <div className="progression-arrow">
            <span>→</span>
          </div>
        )}

        {/* Remaining Items (4 for bot quest, 3 otherwise) */}
        {secondRowItems.length > 0 && (
          <div className="build-phase optional-phase">
            <div className="items-row">
              {secondRowItems.map((item, idx) => (
                <div 
                  key={`item-second-${idx}-${item.id}`} 
                  className="item-slot clickable-item"
                  onClick={() => onItemClick?.(item.id, item.name)}
                  onMouseEnter={(e) => onItemMouseEnter?.(item.id, item.name, e)}
                  onMouseMove={onItemMouseMove}
                  onMouseLeave={onItemMouseLeave}
                  title={item.name}
                >
                  <img 
                    src={getItemImageUrl(item.id)} 
                    alt={item.name}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ItemBuildDisplay;
